import { useEffect, useRef, useState } from "react";
import { Wrench, Plus, Trash, UploadSimple, Copy, DownloadSimple, ArrowSquareIn, FloppyDisk, X, CaretDown, CaretUp, ArrowUp, PencilSimple, Minus, Plus as PlusIcon, Lock, Broom } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, getUser, setUser } from "@/lib/api";
import InfoTip from "@/components/InfoTip";
import { downloadText, copyText } from "@/lib/io";
import { readStaging, writeStaging } from "@/lib/stagingBuffer";
import ResizeHint from "@/components/ResizeHint";
import YTRepliesCard from "@/components/YTRepliesCard";
import YTRepliesConsoleCard from "@/components/YTRepliesConsoleCard";
import YTRepliesRateLimitsCard from "@/components/YTRepliesRateLimitsCard";
import { ConfirmModal, PromptModal } from "@/components/PromptModals";
import DocSaveDialog from "@/components/DocSaveDialog";
import SplitAndPushModal, { needsSplit } from "@/components/SplitAndPushModal";
import TextSearch from "@/components/TextSearch";
import AiBuddy from "@/components/AiBuddy";
import YouTubeTool from "@/components/YouTubeTool";
import RefineryProgressCard from "@/components/RefineryProgressCard";
import RefineryMacros from "@/components/RefineryMacros";
import BoostButton from "@/components/BoostButton";
import CommandHideControl from "@/components/CommandHideControl";
import VoucherTopupModal from "@/components/VoucherTopupModal";
import LLMGuardWrap from "@/components/LLMGuardWrap";
import LockGate from "@/components/LockGate";
import { useCreatorProGate } from "@/components/CreatorProGateProvider";
import { useEntitlements } from "@/lib/entitlements";
import { useTrialKeepPrompt } from "@/lib/trial";

function Slot({ index, value, onChange, onUpload, uploading, active, onToggleActive, rows, onRowsChange }) {
  const fileRef = useRef(null);
  const taRef = useRef(null);

  // Listen for manual textarea resize on ANY of the three slots and broadcast the
  // new row count to the parent so the other two slots sync. Earlier iter only
  // Slot 3 carried the drag handle but the user wanted "any slot resizes all
  // three together" — so all three are now `resize-y` and broadcasting is
  // group-coordinated through onRowsChange.
  useEffect(() => {
    const ta = taRef.current;
    if (!ta || !onRowsChange) return;
    const ro = new ResizeObserver(() => {
      try {
        const lineH = parseFloat(getComputedStyle(ta).lineHeight) || 16;
        const padT = parseFloat(getComputedStyle(ta).paddingTop) || 0;
        const padB = parseFloat(getComputedStyle(ta).paddingBottom) || 0;
        const approxRows = Math.max(3, Math.round((ta.clientHeight - padT - padB) / lineH));
        if (approxRows !== rows) onRowsChange(approxRows);
      } catch { /* noop */ }
    });
    ro.observe(ta);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onRowsChange]);

  return (
    <div className={`relative bg-white border hairline rounded-lg p-4 ${active ? "" : "opacity-60"}`} data-testid={`slot-${index}`} data-slot-active={active ? "true" : "false"}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="font-medium text-sm text-[#1C1B1A] shrink-0 flex items-center gap-1.5">
          Slot {index}
          {!active && (
            <span className="text-[9px] uppercase tracking-[0.18em] bg-[#FDF1ED] text-[#C16E5A] px-1.5 py-0.5 rounded" data-testid={`slot-${index}-inactive-badge`}>inactive</span>
          )}
        </div>
        <div className="flex items-center gap-1.5 flex-1 justify-end min-w-0">
          <span className="text-xs text-[#6B6A68] shrink-0">{(value || "").length.toLocaleString()} chars</span>
          {/* Deactivate / reactivate moved into header (iter-28-bugfix) so the
              native textarea bottom-right resize handle is no longer hijacked. */}
          <button
            type="button"
            onClick={() => onToggleActive(index)}
            data-testid={`slot-${index}-toggle-active`}
            title={active ? "Deactivate — excludes this slot from the next Run" : "Reactivate — includes this slot in the next Run"}
            className={`text-xs w-6 h-6 rounded-full border hairline flex items-center justify-center transition-colors shrink-0 ${active ? "bg-white text-[#6B6A68] hover:text-[#C16E5A] hover:border-[#C16E5A]" : "bg-[#C16E5A] text-white border-[#C16E5A]"}`}
          >
            {active ? <Minus size={12} weight="bold" /> : <PlusIcon size={12} weight="bold" />}
          </button>
          <button onClick={() => fileRef.current?.click()} disabled={uploading || !active} data-testid={`slot-${index}-upload`}
            className="text-xs px-2.5 py-1.5 rounded-md text-[#4A5D4E] hover:bg-[#F0F1EE] flex items-center gap-1 disabled:opacity-50">
            <UploadSimple size={14} weight="duotone" /> {uploading ? "Extracting…" : "Upload"}
          </button>
          {value && (
            <button onClick={() => onChange("")} data-testid={`slot-${index}-clear`}
              className="text-xs px-2 py-1.5 rounded-md text-[#6B6A68] hover:text-[#C16E5A] hover:bg-[#F0F1EE]">
              <X size={14} />
            </button>
          )}
          <input ref={fileRef} type="file" accept=".pdf,.docx,.txt,.md" className="hidden"
            onChange={(e) => onUpload(e.target.files?.[0])} />
        </div>
      </div>
      {value && active && (
        <div className="mb-2"><TextSearch value={value} textareaRef={taRef} testid={`slot-${index}-search`} /></div>
      )}
      <div className="relative">
        <textarea ref={taRef} value={value} onChange={(e) => onChange(e.target.value)} rows={rows} data-testid={`slot-${index}-textarea`}
          disabled={!active}
          className={`w-full bg-[#FDFCF8] border hairline rounded-md px-3 py-2 font-mono text-xs focus:outline-none focus:ring-1 focus:ring-[#4A5D4E] resize-y ${active ? "" : "cursor-not-allowed select-none"}`}
          placeholder={`Paste content into Slot ${index} or upload a file…`} />
        {/* Diagonal strike-through indicator when the slot is deactivated */}
        {!active && (
          <svg
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 w-full h-full"
            preserveAspectRatio="none"
            viewBox="0 0 100 100"
            data-testid={`slot-${index}-strikethrough`}
          >
            <line x1="100" y1="0" x2="0" y2="100" stroke="#C16E5A" strokeWidth="0.6" vectorEffect="non-scaling-stroke" opacity="0.75" />
          </svg>
        )}
      </div>
    </div>
  );
}

export default function FileRefinery({ siloId, siloName, siloSlug }) {
  useTrialKeepPrompt("file_refinery");
  const { locked: cpLocked, lockStyle: cpLockStyle, owns: cpOwns } = useEntitlements();
  const { openGate: cpOpenGate } = useCreatorProGate();
  const [slots, setSlots] = useState({ 1: "", 2: "", 3: "" });
  const [slotActive, setSlotActive] = useState(() => {
    try {
      const saved = JSON.parse(sessionStorage.getItem("workbench_slot_active_filerefinery") || "null");
      if (saved && typeof saved === "object") return { 1: !!saved[1], 2: !!saved[2], 3: !!saved[3] };
    } catch { /* ignore */ }
    return { 1: true, 2: true, 3: true };
  });
  const [slotRows, setSlotRows] = useState(6);
  const [uploading, setUploading] = useState({});
  const [commands, setCommands] = useState([]);
  const [selectedCmdId, setSelectedCmdId] = useState("");
  const [output, setOutput] = useState("");
  const [staging, setStaging] = useState(() => readStaging(`fr_${siloId}`));
  // May 25, 2026 — Voucher top-up modal trigger (from boost gate).
  const [topupOpenSiloId, setTopupOpenSiloId] = useState(null);
  // Cached user record for the YouTube Tool (just the has_youtube_key flag is needed).
  // Refreshed when the YouTubeTool reports a key save/clear.
  const [ytUser, setYtUser] = useState(() => {
    try { return getUser() || {}; } catch { return {}; }
  });
  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const r = await api.get("/auth/me");
        if (!cancel) {
          setYtUser(r.data);
          try { setUser(r.data); } catch { /* ignore */ }
        }
      } catch { /* not authenticated yet */ }
    })();
    return () => { cancel = true; };
  }, []);
  const [running, setRunning] = useState(false);
  // Jul 2026 (Randy) — track the armed Refinery boost so a recorded macro can
  // capture it (mirrors the Workbench). Kept in sync via BoostButton's onBoostChange.
  const [refineryBoost, setRefineryBoost] = useState(null);

  // ---- Macro record & replay (Refinery flavor, Jul 2026) ----
  // Unlike the Workbench, the Refinery's Run is a BACKGROUND job, so the macro
  // player awaits job completion before advancing. Recording buffer + timing
  // live in refs so keystroke events don't trigger re-renders.
  const [macroMode, setMacroMode] = useState("idle");
  const [macroRecordCount, setMacroRecordCount] = useState(0);
  const recordingRef = useRef(false);
  const recordBufRef = useRef([]);
  const lastStepAtRef = useRef(0);
  const macroStepLabel = (type, params) => {
    switch (type) {
      case "yt_fetch": return `Fetch YouTube ${params.kind === "metadata" ? "description/title" : params.kind}`;
      case "yt_transfer": return `Send YouTube data → ${params.target === "staging" ? "Staging" : params.target === "assistant" ? "Assistant" : "Slot 1"}`;
      case "run": return `Run "${params.command_name || "operation"}"`;
      case "set_output_size": return `Set output size → ${params.pct == null ? "Auto" : params.pct + "%"}`;
      case "set_destination": return `When done → ${(!params.tier || params.tier === 0) ? "Stage only" : `Commit to T${params.tier}`}`;
      case "pause_for_editing": return "Pause for editing";
      default: return type;
    }
  };
  const recordStep = (type, params = {}) => {
    if (!recordingRef.current) return;
    const buf = recordBufRef.current;
    const now = Date.now();
    const delay = lastStepAtRef.current ? Math.min(8000, Math.max(300, now - lastStepAtRef.current)) : 500;
    // Slider drags + destination changes collapse to their final value; consecutive edit-pauses collapse to one.
    if ((type === "set_output_size" || type === "set_destination") && buf.length && buf[buf.length - 1].type === type) {
      buf[buf.length - 1].params = params;
      buf[buf.length - 1].label = macroStepLabel(type, params);
      lastStepAtRef.current = now; return;
    }
    if (type === "pause_for_editing" && buf.length && buf[buf.length - 1].type === "pause_for_editing") {
      lastStepAtRef.current = now; return;
    }
    lastStepAtRef.current = now;
    buf.push({ type, params, label: macroStepLabel(type, params), delay_ms: delay });
    setMacroRecordCount(buf.length);
  };

  // Sequential chunked processing — iter-28-bugfix made each slot's offset
  // independent so unequal slot lengths don't break compare runs (the 4231 vs
  // 6035 bug). chunkOffset (single number) kept as a back-compat read of the
  // hydration payload but the source of truth is now `chunkOffsets` (per-slot).
  const [chunkOffset, setChunkOffset] = useState(0);
  const [chunkOffsets, setChunkOffsets] = useState({ 1: 0, 2: 0, 3: 0 });
  const [lockedChunkSize, setLockedChunkSize] = useState(0);
  const [lastChunkRanges, setLastChunkRanges] = useState(null);

  // iter-35c: File Refinery is refinery-only — synchronous /workbench/run is
  // never used. The refinery toggle is hard-set ON; the UI exposes per-slot vs
  // concat mode and an optional auto-commit-to-tier dropdown instead.
  const refineryMode = true;
  const [activeRefineryJobId, setActiveRefineryJobId] = useState(null);
  // 'per_slot' (default) → each non-empty slot becomes its own iteration
  // block, streaming separate distilled outputs labeled "Slot 1/2/3".
  // 'concat'           → merge active slots into one block, single pass.
  const [refineryMixMode, setRefineryMixMode] = useState("per_slot");
  // 0 = stage only (review then manual commit). 1/2/3 = auto-commit to that
  // tier of the current silo on success.
  const [autoCommitTier, setAutoCommitTier] = useState(0);
  // Jul 2026 (Randy) — per-chunk output-size control (like the Workbench).
  // null = Auto (the operation decides). Gated by the `output_size` SKU.
  const [outputPct, setOutputPct] = useState(null);
  // Reset the slider to the picked command's default whenever the op changes.
  useEffect(() => {
    const cmd = commands.find((c) => c.id === selectedCmdId);
    setOutputPct(cmd?.default_output_pct ?? null);
  }, [selectedCmdId, commands]);

  // Customize dialog state
  const [showCustomize, setShowCustomize] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [draftCmd, setDraftCmd] = useState({ name: "", instructions: "" });
  const [savingCmd, setSavingCmd] = useState(false);

  // Modal state (replaces window.confirm/prompt — Mac/Chrome silently blocks repeat dialogs)
  const [confirmState, setConfirmState] = useState({ open: false });
  const [promptState, setPromptState] = useState({ open: false });
  const [commitState, setCommitState] = useState({ open: false, text: "", defaultTitle: "" });
  const [splitState, setSplitState] = useState({ open: false, content: "", siloId: "", tier: 2, title: "" });

  const stagingRef = useRef(null);

  // ---- Server-side workbench-state persistence (iter-20) ----
  // Survives tab close, logout, device switch. Strictly silo-isolated by the backend
  // (composite key user_id + silo_id). On siloId change we hydrate from server
  // (fast cache: sessionStorage is still primed so UX never sees a flash of empty).
  // Saves are debounced 800ms after last edit. Remote updates always win on mount.
  const [hydrating, setHydrating] = useState(true);
  const initialHydrationRef = useRef(false);
  const saveTimerRef = useRef(null);
  const lastSavedRef = useRef({});

  // Hydrate from server on mount AND whenever siloId changes.
  useEffect(() => {
    let cancelled = false;
    setHydrating(true);
    (async () => {
      try {
        const r = await api.get(`/silos/${siloId}/workbench-state?surface=file_refinery`);
        if (cancelled) return;
        const s = r.data || {};
        setStaging(s.staging || "");
        setOutput(s.output || "");
        setSlots({ 1: s.slot1 || "", 2: s.slot2 || "", 3: s.slot3 || "" });
        if (s.slot_active) setSlotActive({ 1: !!s.slot_active["1"], 2: !!s.slot_active["2"], 3: !!s.slot_active["3"] });
        setChunkOffset(Number(s.chunk_offset || 0));
        // Per-slot offsets (iter-28). Back-compat: if server only has the old
        // single chunk_offset, fan it out to all 3 so the user picks up where
        // they left off without losing work.
        const co = s.chunk_offsets || {};
        const fallback = Number(s.chunk_offset || 0);
        setChunkOffsets({
          1: Number(co["1"] ?? fallback),
          2: Number(co["2"] ?? fallback),
          3: Number(co["3"] ?? fallback),
        });
        setLockedChunkSize(Number(s.locked_chunk_size || 0));
        setLastChunkRanges(s.last_chunk_ranges || null);
        setSlotRows(Number(s.slot_rows || 6));
        // Mirror to sessionStorage so cross-tab + Operations recall keep working.
        writeStaging(`fr_${siloId}`, s.staging || "");
        lastSavedRef.current = {
          staging: s.staging || "",
          output: s.output || "",
          slot1: s.slot1 || "", slot2: s.slot2 || "", slot3: s.slot3 || "",
          slot_active: s.slot_active || { "1": true, "2": true, "3": true },
          chunk_offset: Number(s.chunk_offset || 0),
          locked_chunk_size: Number(s.locked_chunk_size || 0),
          last_chunk_ranges: s.last_chunk_ranges || null,
          slot_rows: Number(s.slot_rows || 6),
        };
        initialHydrationRef.current = true;
        setHydrating(false);
      } catch {
        // Network blip: keep whatever is in sessionStorage; mark as hydrated so
        // saves can resume.
        initialHydrationRef.current = true;
        setHydrating(false);
      }
    })();
    return () => { cancelled = true; if (saveTimerRef.current) { clearTimeout(saveTimerRef.current); saveTimerRef.current = null; } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siloId]);

  // Persist staging to sessionStorage so the Output Recirculation Buffer (Operations
  // tab) can append while the Workbench is unmounted, and cross-tab updates work.
  useEffect(() => { writeStaging(`fr_${siloId}`, staging); }, [staging, siloId]);
  useEffect(() => {
    const onExternal = (e) => {
      if (!e?.detail || e.detail.siloId !== `fr_${siloId}`) return;
      const next = readStaging(`fr_${siloId}`);
      setStaging((prev) => (prev === next ? prev : next));
    };
    window.addEventListener("staging-updated", onExternal);
    return () => window.removeEventListener("staging-updated", onExternal);
  }, [siloId]);

  // Debounced server save: any time staging/output/slots/etc change, we PATCH the
  // diff to the backend after 800ms of quiet. Skip during initial hydration so we
  // don't immediately overwrite the freshly-loaded state with React defaults.
  useEffect(() => {
    if (!initialHydrationRef.current || hydrating) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      const current = {
        staging,
        output,
        slot1: slots[1] || "", slot2: slots[2] || "", slot3: slots[3] || "",
        slot_active: { "1": !!slotActive[1], "2": !!slotActive[2], "3": !!slotActive[3] },
        chunk_offset: chunkOffset,  // back-compat single int
        chunk_offsets: { "1": chunkOffsets[1], "2": chunkOffsets[2], "3": chunkOffsets[3] },
        locked_chunk_size: lockedChunkSize,
        last_chunk_ranges: lastChunkRanges,
        slot_rows: slotRows,
      };
      const diff = {};
      for (const k of Object.keys(current)) {
        if (JSON.stringify(current[k]) !== JSON.stringify(lastSavedRef.current[k])) {
          diff[k] = current[k];
        }
      }
      if (Object.keys(diff).length === 0) return;
      try {
        await api.patch(`/silos/${siloId}/workbench-state?surface=file_refinery`, diff);
        lastSavedRef.current = { ...lastSavedRef.current, ...diff };
      } catch { /* swallow — next change will retry */ }
    }, 800);
    return () => { if (saveTimerRef.current) { clearTimeout(saveTimerRef.current); saveTimerRef.current = null; } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [staging, output, slots, slotActive, chunkOffset, chunkOffsets, lockedChunkSize, lastChunkRanges, slotRows, siloId]);

  const askConfirm = (opts) => setConfirmState({ open: true, ...opts });
  const askPrompt = (opts) => setPromptState({ open: true, ...opts });

  // Jul 2026 (Randy) — "Clear Refinery" (mirrors the Workbench's Clear button)
  // and transparent top/bottom quick-nav carets for the long Refinery page.
  const [childClearNonce, setChildClearNonce] = useState(0);
  const clearRefinery = () => {
    askConfirm({
      title: "Clear the whole Refinery?",
      message: "This clears all three slots, the staging area, the Result/output, and the YouTube tool's video URL + output. This can't be undone.",
      danger: true,
      confirmLabel: "Clear everything",
      onConfirm: () => {
        setSlots({ 1: "", 2: "", 3: "" });
        setSlotActive({ 1: true, 2: true, 3: true });
        setStaging("");
        setOutput("");
        setChildClearNonce((n) => n + 1);
        toast.success("Refinery cleared.");
      },
    });
  };
  const scrollRefineryToTop = () => {
    document.querySelector('[data-testid="fr-top-anchor"]')?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const scrollRefineryToBottom = () => {
    document.querySelector('[data-testid="fr-bottom-anchor"]')?.scrollIntoView({ behavior: "smooth", block: "end" });
  };

  const loadCommands = async () => {
    try {
      const r = await api.get("/commands?scope=refine_silo");
      setCommands(r.data);
      setSelectedCmdId((cur) => {
        if (cur && r.data.some((c) => c.id === cur)) return cur;
        const firstGlobal = r.data.find((c) => c.is_global);
        return (firstGlobal || r.data[0])?.id || "";
      });
    } catch { toast.error("Failed to load commands"); }
  };
  useEffect(() => { loadCommands(); }, []);

  // Pick up "Send to Workbench" seed from Library and append to staging area
  // Polls sessionStorage to support cross-tab arrivals while Workbench stays mounted.
  useEffect(() => {
    const consume = () => {
      try {
        const raw = sessionStorage.getItem("file_refinery_seed");
        if (!raw) return;
        const seed = JSON.parse(raw);
        sessionStorage.removeItem("file_refinery_seed");
        if (seed?.staging && seed?.content) {
          // ALWAYS append to the END of whatever's already in staging.
          setStaging((s) => (s ? `${s}\n\n${seed.content}` : seed.content));
          setTimeout(() => stagingRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 100);
          toast.success(`Appended "${seed.title || "document"}" to staging.`);
        } else if (seed?.slot && seed?.content) {
          setSlots((s) => ({ ...s, [seed.slot]: seed.content }));
        }
      } catch {}
    };
    consume();
    const id = setInterval(consume, 750);
    return () => clearInterval(id);
  }, []);

  const onSlotUpload = async (n, file) => {
    if (!file) return;
    setUploading((u) => ({ ...u, [n]: true }));
    const form = new FormData(); form.append("file", file);
    try {
      const r = await api.post("/workbench/extract", form, { headers: { "Content-Type": "multipart/form-data" } });
      setSlots((s) => ({ ...s, [n]: r.data.text }));
      toast.success(`Loaded ${r.data.char_count.toLocaleString()} chars from ${file.name}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not extract file"); }
    finally { setUploading((u) => ({ ...u, [n]: false })); }
  };

  const totalChars = (slotActive[1] ? slots[1]?.length || 0 : 0) + (slotActive[2] ? slots[2]?.length || 0 : 0) + (slotActive[3] ? slots[3]?.length || 0 : 0);
  const populatedCount = [1, 2, 3].filter((n) => slotActive[n] && slots[n] && slots[n].trim()).length;
  const populatedSlots = [1, 2, 3].filter((n) => slotActive[n] && slots[n] && slots[n].trim());
  const maxLen = Math.max(0, ...populatedSlots.map((n) => slots[n].length));

  // iter-35d: live cost preview pill. Calls /api/refinery/jobs/preview
  // (debounced 600ms) whenever slots OR mix mode change so creators see
  // "≈ N chunks · ~$0.04" before they commit. No LLM tokens consumed by
  // preview — server returns chunk_count + est_cost_cents from char math.
  const [costPreview, setCostPreview] = useState(null);
  const [costPreviewLoading, setCostPreviewLoading] = useState(false);
  const previewTimerRef = useRef(null);
  useEffect(() => {
    if (previewTimerRef.current) clearTimeout(previewTimerRef.current);
    if (populatedCount === 0 || !!activeRefineryJobId) {
      setCostPreview(null);
      return;
    }
    previewTimerRef.current = setTimeout(async () => {
      setCostPreviewLoading(true);
      try {
        const r = await api.post("/refinery/jobs/preview", {
          source_type: "slots",
          source_config: {
            silo_id: siloId,
            surface: "file_refinery",
            mode: refineryMixMode,
          },
        });
        setCostPreview(r.data);
      } catch { setCostPreview(null); }
      finally { setCostPreviewLoading(false); }
    }, 600);
    return () => { if (previewTimerRef.current) clearTimeout(previewTimerRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slots[1], slots[2], slots[3], slotActive[1], slotActive[2], slotActive[3], refineryMixMode, populatedCount, activeRefineryJobId, siloId]);

  // Persist slot-active state across SiloManage tab switches.
  useEffect(() => {
    try { sessionStorage.setItem("workbench_slot_active_filerefinery", JSON.stringify(slotActive)); } catch { /* ignore */ }
  }, [slotActive]);

  const toggleSlotActive = (n) => {
    setSlotActive((s) => ({ ...s, [n]: !s[n] }));
  };

  const computeChunkSize = (n) => (n === 1 ? 10000 : n === 2 ? 5000 : n === 3 ? 3000 : 0);
  const liveChunkSize = lockedChunkSize || computeChunkSize(populatedCount);

  // iter-28-bugfix: each populated slot iterates INDEPENDENTLY. A slot is
  // "done" when its own offset >= its own length. The job is "complete" only
  // when every active populated slot has reached its end. Inactive or empty
  // slots are ignored. Single shared chunkOffset is kept in sync as the MIN
  // active offset for back-compat (some UI bits still use it).
  const isFirstChunk = chunkOffsets[1] === 0 && chunkOffsets[2] === 0 && chunkOffsets[3] === 0;
  const slotIsDone = (n) => !slotActive[n] || !slots[n] || !slots[n].trim() || (chunkOffsets[n] || 0) >= (slots[n]?.length || 0);
  const isComplete = lockedChunkSize > 0 && populatedSlots.length > 0 && populatedSlots.every(slotIsDone);

  const slotRange = (n) => {
    if (!slotActive[n]) return null;
    const len = slots[n]?.length || 0;
    const off = chunkOffsets[n] || 0;
    if (off >= len) return null;
    const end = Math.min(off + liveChunkSize, len);
    return { start: off + 1, end, total: len };
  };

  const startOver = () => {
    setChunkOffset(0);
    setChunkOffsets({ 1: 0, 2: 0, 3: 0 });
    setLockedChunkSize(0);
    setLastChunkRanges(null);
    setOutput("");
  };

  useEffect(() => {
    if (lockedChunkSize > 0 && computeChunkSize(populatedCount) !== lockedChunkSize) startOver();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [populatedCount]);

  // Submit a refinery job for the current operation/output/destination. Returns
  // the created job payload ({id, chunk_count, est_cost_cents}) or throws the
  // raw axios error so callers can branch on the detail code.
  const submitRefineryJob = async ({ confirmedOverCap = false } = {}) => {
    const selectedCmd = commands.find((c) => c.id === selectedCmdId);
    if (!selectedCmd) throw new Error("Select an operation first.");
    const r = await api.post("/refinery/jobs", {
      source_type: "slots",
      source_config: { silo_id: siloId, surface: "file_refinery", mode: refineryMixMode },
      command_id: selectedCmd.id,
      silo_id: siloId,
      output_pct: outputPct,
      auto_commit_to_tier: autoCommitTier > 0 ? autoCommitTier : null,
      confirmed_over_cap: !!confirmedOverCap,
    });
    return r.data;
  };

  const run = async () => {
    if (populatedCount === 0) return toast.error("Fill at least one slot.");
    const selectedCmd = commands.find((c) => c.id === selectedCmdId);
    if (!selectedCmd) return toast.error("Select an operation first.");
    recordStep("run", {
      command_id: selectedCmd.id,
      command_name: selectedCmd.name,
      // Jul 2026 (Randy) — capture the armed Boost (model + output target) so a
      // recorded Refinery macro replays on the SAME power model. Editable later.
      boost: refineryBoost?.target_model ? {
        target_model: refineryBoost.target_model,
        target_provider: refineryBoost.target_provider || null,
        target_max_tokens: refineryBoost.target_max_tokens || null,
        desired_output_chars: refineryBoost.desired_output_chars || null,
      } : null,
    });
    const startJob = async (confirmedOverCap) => {
      const data = await submitRefineryJob({ confirmedOverCap });
      setActiveRefineryJobId(data.id);
      toast.success(`Refinery started · ${data.chunk_count} chunks · ~$${(data.est_cost_cents / 100).toFixed(2)}`);
    };
    try { await startJob(false); }
    catch (e) {
      const det = e.response?.data?.detail;
      const status = e.response?.status;
      if (status === 402 && det?.code === "SILO_VOUCHER_EMPTY") {
        toast.error(det.message || "Silo voucher is empty.");
        setTopupOpenSiloId(det.silo_id || siloId);
      } else if (det?.code === "cost_cap_exceeded") {
        if (window.confirm(`${det.message}\n\nProceed anyway?`)) {
          try { await startJob(true); }
          catch (e2) {
            const det2 = e2.response?.data?.detail;
            if (e2.response?.status === 402 && det2?.code === "SILO_VOUCHER_EMPTY") {
              toast.error(det2.message || "Silo voucher is empty.");
              setTopupOpenSiloId(det2.silo_id || siloId);
            } else {
              toast.error(det2 || "Refinery start failed.");
            }
          }
        }
      } else if (det?.code === "insufficient_balance") {
        toast.error(det.message);
      } else {
        toast.error(typeof det === "string" ? det : "Refinery start failed.");
      }
    }
  };

  // Macro-driven run: starts a job and AWAITS its background completion by
  // polling the same job the progress card shows. Returns the terminal status
  // so the macro player can flag skipped/lost chunks. Throws a tagged error
  // (__refinery_skip) when the file is too big for one job or the voucher is
  // empty — the macro surfaces those in its "gaps" alert instead of a modal.
  const runRefineryJobAwaitable = async () => {
    let data;
    try {
      data = await submitRefineryJob({ confirmedOverCap: false });
    } catch (e) {
      const det = e.response?.data?.detail;
      const status = e.response?.status;
      if (det?.code === "cost_cap_exceeded") {
        const err = new Error("this file is too big/expensive for one refinery job");
        err.__refinery_skip = true;
        err.code = "REFINERY_TOOBIG";
        err.reason = "This file is too big (or too expensive) to finish in one job. Arm Boost so it processes in bigger pieces, raise the per-job cost cap, or run it manually and confirm the cost — then this macro will sail through.";
        throw err;
      }
      if (status === 402 && det?.code === "SILO_VOUCHER_EMPTY") {
        setTopupOpenSiloId(det?.silo_id || siloId);
        const err = new Error("the silo's voucher is empty");
        err.__refinery_skip = true;
        err.code = "VOUCHER";
        err.reason = "The silo's voucher is empty, so the refinery can't run. Top it up, then run the macro again.";
        throw err;
      }
      throw new Error(typeof det === "string" ? det : "Refinery start failed.");
    }
    setActiveRefineryJobId(data.id);
    const terminal = ["completed", "completed_with_gaps", "cancelled", "error"];
    // Poll until the background job reaches a terminal state.
    // eslint-disable-next-line no-constant-condition
    while (true) {
      await new Promise((r) => setTimeout(r, 3000));
      let jr;
      try { jr = await api.get(`/refinery/jobs/${data.id}`); } catch { continue; }
      const j = jr.data || {};
      if (terminal.includes(j.status)) {
        return {
          status: j.status,
          chunks_skipped: j.chunks_skipped || 0,
          error: j.error,
          auto_commit_to_tier: j.auto_commit_to_tier,
        };
      }
    }
  };

  // iter-35c: synchronous /workbench/run is unused on the File Refinery
  // surface. doRun is retained as a no-op shim only to avoid breaking any
  // legacy callers; nothing in this component invokes it.
  const doRun = async () => { /* noop — refinery-only surface */ };

  // Output / staging actions
  const sendOutputToSlot = (n) => { if (!output) return; setSlots((s) => ({ ...s, [n]: output })); toast.success(`Output sent to Slot ${n}.`); };
  const sendStagingToSlot = (n) => { if (!staging) return; setSlots((s) => ({ ...s, [n]: staging })); toast.success(`Staging sent to Slot ${n}.`); };

  const appendToStaging = () => {
    if (!output) return;
    setStaging((s) => (s ? `${s}\n\n${output}` : output));
    toast.success("Appended to staging area.");
  };

  const handleDownload = async (filename, text) => {
    const ok = await downloadText(filename, text);
    toast[ok ? "success" : "error"](ok ? "Download started." : "Could not start download.");
  };
  const handleCopy = async (text) => {
    const ok = await copyText(text);
    toast[ok ? "success" : "error"](ok ? "Copied to clipboard." : "Could not copy.");
  };
  const handleCommit = (text, defaultTitle) => {
    if (!text?.trim()) return;
    setCommitState({ open: true, text, defaultTitle: defaultTitle || "" });
  };
  // Commit is handled entirely by <DocSaveDialog> in commit mode (dup-name guard
  // + Hide + Split-&-Push handoff), pinned to the current silo.

  // Customize CRUD
  const openCreate = () => { setEditingId(null); setDraftCmd({ name: "", instructions: "" }); };
  const openEdit = (cmd) => { setEditingId(cmd.id); setDraftCmd({ name: cmd.name, instructions: cmd.instructions }); };
  const cancelEdit = () => { setEditingId(null); setDraftCmd({ name: "", instructions: "" }); };

  const saveDraft = async () => {
    if (!draftCmd.name.trim() || !draftCmd.instructions.trim()) return toast.error("Name and instructions required.");
    setSavingCmd(true);
    try {
      if (editingId) {
        const r = await api.patch(`/commands/${editingId}`, draftCmd);
        toast.success(`Command "${r.data.name}" updated.`);
      } else {
        const r = await api.post("/commands", { ...draftCmd, scope: "refine_silo" });
        toast.success(`Command "${r.data.name}" saved.`);
        setSelectedCmdId(r.data.id);
      }
      setEditingId(null); setDraftCmd({ name: "", instructions: "" });
      await loadCommands();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save command"); }
    finally { setSavingCmd(false); }
  };

  const deleteCustom = (cmd) => {
    if (cmd.is_global) return;
    askConfirm({
      title: `Delete "${cmd.name}"?`,
      message: "This cannot be undone.",
      danger: true,
      confirmLabel: "Delete",
      onConfirm: async () => {
        try { await api.delete(`/commands/${cmd.id}`); await loadCommands(); if (editingId === cmd.id) cancelEdit(); toast.success("Deleted."); }
        catch { toast.error("Delete failed"); }
      },
    });
  };

  const builtIn = commands.filter((c) => c.is_global);
  const custom = commands.filter((c) => !c.is_global);
  // Jun 2026 (Randy): nothing the platform generates may travel with the
  // content. A "-- STAGING AREA --" banner used to be prepended to every
  // export and commit, which baked it into silo files and then into answers.
  // Exports carry the text only. Titles are still derived from the first ~10
  // words so the user doesn't have to type one (still editable in the modal).
  const firstTenWords = (text) => {
    const words = (text || "").trim().split(/\s+/).filter(Boolean).slice(0, 10);
    return words.join(" ").slice(0, 120) || "Staged content";
  };

  const ActionRow = ({ text, testidPrefix, defaultTitle, sendTo, extra, isStaging = false }) => {
    const exportText = text;
    const exportTitle = isStaging ? firstTenWords(text) : defaultTitle;
    return (
      <div className="mt-3 flex flex-wrap gap-2" data-testid={`${testidPrefix}-actions`}>
        <button onClick={() => handleDownload(`${testidPrefix}-${Date.now()}.txt`, exportText)} data-testid={`${testidPrefix}-download`}
          className="text-sm px-4 py-2 rounded-md border hairline bg-white hover:border-[#4A5D4E] hover:text-[#4A5D4E] flex items-center gap-1.5"><DownloadSimple size={14} /> Download .txt</button>
        <button onClick={() => handleCopy(exportText)} data-testid={`${testidPrefix}-copy`}
          className="text-sm px-4 py-2 rounded-md border hairline bg-white hover:border-[#4A5D4E] hover:text-[#4A5D4E] flex items-center gap-1.5"><Copy size={14} /> Copy</button>
        <button onClick={() => handleCommit(exportText, exportTitle)} data-testid={`${testidPrefix}-commit`}
          className="text-sm px-4 py-2 rounded-md bg-[#4A5D4E] text-white hover:bg-[#3A4A3E] flex items-center gap-1.5"><FloppyDisk size={14} /> Commit to silo</button>
        {extra}
        <div className="w-px h-8 bg-[#E5E5E0] mx-1" />
        {[1].map((n) => (
          <button key={n} onClick={() => sendTo(n)} data-testid={`${testidPrefix}-send-slot-${n}`}
            className="text-sm px-4 py-2 rounded-md border hairline bg-white hover:border-[#4A5D4E] hover:text-[#4A5D4E] flex items-center gap-1.5">
            <ArrowSquareIn size={14} /> Send to Slot {n}
          </button>
        ))}
      </div>
    );
  };

  // ---- Macro bus + YouTube macro API (reassigned every render so the player
  // always reads/writes fresh state) ----
  const ytApiRef = useRef(null);
  const macroApi = { recordingRef, record: recordStep, registerApi: (a) => { ytApiRef.current = a; } };
  const macroBusRef = useRef({});
  // Feb 17, 2026 (Randy pt-macro-selected) — bridge for the YouTube Tool's
  // "Run selected Macro" button on the Refinery surface. RefineryMacros
  // registers its selected-macro helpers on this ref every render; YouTubeTool
  // reads them on click.
  const refineryMacroApiRef = useRef(null);
  macroBusRef.current = {
    getState: () => ({ staging, running: !!activeRefineryJobId, selectedCmdId, destination: autoCommitTier }),
    selectOperation: (id) => setSelectedCmdId(id),
    setOutputSize: (pct) => setOutputPct(pct),
    setDestination: (tier) => setAutoCommitTier(Number(tier) || 0),
    getCommands: () => commands,
    runRefineryJob: async () => await runRefineryJobAwaitable(),
    clearStaging: () => setStaging(""),
    // Jul 2026 (Randy) — total chars across active/populated slots, for the
    // macro's heavy-model cost estimate.
    getInputChars: () => totalChars,
    // Arm a persistent Boost for the WHOLE macro run (surface-scoped to the
    // Refinery). The background job picks up the active boost automatically.
    // Reverted/cancelled when the macro ends.
    armBoostForMacro: async (boostCfg) => {
      if (!boostCfg?.target_model) return null;
      const r = await api.post("/boost/start", {
        surface: "refinery",
        target_model: boostCfg.target_model,
        target_provider: boostCfg.target_provider,
        target_max_tokens: boostCfg.target_max_tokens || 8192,
        duration_min: 240,
        cost_cap_cents: 5000,
        just_this_run: false,
        surface_silo_id: siloId || null,
        desired_output_chars: boostCfg.desired_output_chars || 0,
      });
      setRefineryBoost({ ...r.data });
      return r.data;
    },
    cancelBoostForMacro: async () => {
      try {
        const a = await api.get("/boost/active");
        const mine = (a.data?.sessions || []).find((s) => s.surface === "refinery");
        if (mine) await api.post(`/boost/${mine.id}/cancel`);
      } catch { /* ignore */ }
      setRefineryBoost(null);
    },
    ytApiRef,
  };

  return (
    <div data-testid="file-refinery-tab">
      <div data-testid="fr-top-anchor" className="scroll-mt-24" />
      <div className="flex items-center gap-2 mb-1">
        <Wrench size={20} weight="duotone" className="text-[#4A5D4E]" />
        {/* Iter160m (Randy pt-siloart-loosetext-sweep) — inner <span>
            was hardcoded green (#4A5D4E), which visually competed with
            the Silo Art V2 whitetext override on dark art. Dropped the
            inline color so the span inherits from the whitetext rule
            (or the parent H2's base #1C1B1A when V2 whitetext is off). */}
        <h2 className="font-serif-display text-3xl text-[#1C1B1A]">File Refinery — <span className="silo-loose">The Big-File tool</span></h2>
        <InfoTip text="Every everyday operation — Accurately Condense, Summarize (Retain 100%), and the rest — built to chew through a file of ANY size, dependably, in the background. It slices a huge file into pieces, processes them one at a time with auto-retries, saves progress as it goes, and drops the result here. Use this when the file is too big for the Workbench (or you just want hands-off, reliable processing). The Workbench is the tool for comparing multiple files at once." />
        <div className="ml-auto shrink-0 flex items-center gap-2">
          {/* Feb 2026 (Randy pt-yt-macros-jump) — mirror of Workbench's
              jump-to-Macros chevron. Refinery's own Macros anchor below. */}
          <button
            onClick={() => {
              const el = document.getElementById("refinery-macros-anchor");
              if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
            }}
            data-testid="refinery-jump-macros"
            title="Jump to Macros"
            className="text-xs px-2 py-1.5 rounded-md border hairline bg-white text-[#4A5D4E] hover:bg-[#F0F1EE] flex items-center gap-1.5">
            <CaretDown size={14} weight="bold" /> Macros
          </button>
          <button onClick={clearRefinery} data-testid="refinery-clear-all"
            title="Clear all slots, staging, result, and the YouTube tool's URL + output"
            className="text-xs px-3 py-1.5 rounded-md border hairline bg-white text-[#C16E5A] hover:bg-[#FDF1ED] hover:border-[#C16E5A] flex items-center gap-1.5 shrink-0">
            <Broom size={14} weight="duotone" /> Clear Refinery
          </button>
        </div>
      </div>
      <p className="text-sm text-[#6B6A68] mb-6">Every everyday operation, built to chew through a file of <strong>any size</strong>, dependably, in the background. Drop a big file into the slot, pick an operation, choose where the result should land, and let it run.</p>

      <div className="grid grid-cols-1 gap-4">
        <Slot key={1} index={1} value={slots[1]} uploading={!!uploading[1]}
          active={slotActive[1]}
          onToggleActive={toggleSlotActive}
          rows={slotRows}
          onRowsChange={setSlotRows}
          onChange={(v) => setSlots((s) => ({ ...s, 1: v }))}
          onUpload={(f) => onSlotUpload(1, f)} />
      </div>

      <div className="mt-5 bg-white border hairline rounded-lg p-4 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 flex-1 min-w-[260px]">
          <span className="text-xs uppercase tracking-[0.2em] text-[#6B6A68]">Operation</span>
          <div className="relative flex-1">
            <select data-testid="file-refinery-command" value={selectedCmdId} onChange={(e) => setSelectedCmdId(e.target.value)}
              className="appearance-none w-full bg-[#FDFCF8] border hairline rounded-md px-3 py-2 pr-9 text-sm focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]">
              {/* Jun 2026 (Randy) — custom operations above the built-ins here
                  too; the Refinery is where this tool has its roots. */}
              {custom.length > 0 && (
                <optgroup label="Your custom commands">
                  {custom.map((c) => <option key={c.id} value={c.id} title={c.instructions}>{c.name}</option>)}
                </optgroup>
              )}
              <optgroup label="Global (built-in)">
                {builtIn.map((c) => <option key={c.id} value={c.id} title={c.instructions}>{c.name}</option>)}
              </optgroup>
            </select>
            <CaretDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6B6A68] pointer-events-none" />
          </div>
          {/* May 25, 2026 — Per-user hide/unhide for refinery commands. */}
          <CommandHideControl
            scope="refine_silo"
            selectedCmd={commands.find((c) => c.id === selectedCmdId)}
            onReload={loadCommands}
          />
        </div>
        <button onClick={() => { openCreate(); setShowCustomize(true); }} data-testid="file-refinery-customize"
          className="text-sm px-4 py-2 rounded-md border hairline bg-white hover:border-[#4A5D4E] hover:text-[#4A5D4E] flex items-center gap-1.5">
          <Plus size={14} /> Customize
        </button>
        <div className="text-xs text-[#6B6A68]">{totalChars.toLocaleString()} chars</div>
        {/* iter-35d: live cost preview pill — updates 600ms after the user
            stops typing/mode-toggling. Surfaces chunk_count + est_cost_cents
            BEFORE the run so creators don't bounce on click. */}
        {costPreview && populatedCount > 0 && (
          <span
            className="text-[10px] uppercase tracking-[0.18em] px-2 py-1 rounded-md bg-[#F0F1EE] text-[#4A5D4E] border hairline tabular-nums"
            data-testid="file-refinery-cost-preview"
            title={`${costPreview.block_count} block${costPreview.block_count === 1 ? "" : "s"} · ${costPreview.total_chars.toLocaleString()} chars · ${costPreview.would_exceed_cap ? "EXCEEDS your per-job cap" : "within cap"}`}
          >
            {costPreviewLoading ? "Estimating…"
              : `≈ ${costPreview.chunk_count} chunk${costPreview.chunk_count === 1 ? "" : "s"} · ~$${(costPreview.est_cost_cents / 100).toFixed(2)}`}
            {costPreview.would_exceed_cap && <span className="ml-1.5 text-[#C16E5A]">⚠ over cap</span>}
          </span>
        )}
        {/* iter-35c: per-slot vs concat mode removed Jul 2026 (Randy) — the
            Refinery is now a single input, so there's nothing to mix. */}
        {/* Output size — per-chunk target as a % of input. Gated by output_size. */}
        {cpOwns("output_size") ? (
          <label className="text-xs text-[#6B6A68] flex items-center gap-1.5" title="How large each chunk's distilled output should be, as a % of that chunk's input. Auto lets the operation decide.">
            <span className="uppercase tracking-[0.18em] text-[10px]">Output</span>
            {outputPct == null ? (
              <button type="button" onClick={() => { recordStep("set_output_size", { pct: 50 }); setOutputPct(50); }} data-testid="file-refinery-output-auto"
                className="text-xs px-2 py-1 rounded-md border hairline bg-[#FDFCF8] hover:border-[#4A5D4E]">Auto</button>
            ) : (
              <span className="flex items-center gap-1.5">
                <input type="range" min={10} max={100} step={5} value={outputPct}
                  onChange={(e) => { const v = parseInt(e.target.value, 10); recordStep("set_output_size", { pct: v }); setOutputPct(v); }}
                  data-testid="file-refinery-output-slider" className="w-24 accent-[#4A5D4E]" disabled={!!activeRefineryJobId} />
                <span className="tabular-nums w-8 text-[#4A5D4E]">{outputPct}%</span>
                <button type="button" onClick={() => { recordStep("set_output_size", { pct: null }); setOutputPct(null); }} className="text-[10px] underline text-[#6B6A68]">auto</button>
              </span>
            )}
          </label>
        ) : (
          <button type="button" onClick={() => cpOpenGate("output_size")} data-testid="file-refinery-output-locked"
            className="text-xs px-2 py-1 rounded-md border hairline bg-[#FDFCF8] text-[#A89360] hover:border-[#A89360] flex items-center gap-1"
            title="Custom output size is a Creator Pro add-on — click to unlock">
            Output size <Lock size={11} weight="fill" />
          </button>
        )}
        {/* When done — stage only or auto-commit to a tier of THIS silo. */}
        <label className="text-xs text-[#6B6A68] flex items-center gap-1.5" title="Where the refinery output should land.">
          <span className="uppercase tracking-[0.18em] text-[10px]">When done</span>
          <select
            value={autoCommitTier}
            onChange={(e) => { const v = parseInt(e.target.value, 10); recordStep("set_destination", { tier: v }); setAutoCommitTier(v); }}
            data-testid="file-refinery-when-done"
            className="bg-[#FDFCF8] border hairline rounded-md px-2 py-1 text-xs"
            disabled={!!activeRefineryJobId}
          >
            <option value={0}>Stage only</option>
            <option value={1}>Commit to T1 (Anchor)</option>
            <option value={2}>Commit to T2 (Logic)</option>
            <option value={3}>Commit to T3 (Library)</option>
          </select>
        </label>
        <div className="ml-auto">
          <LockGate locked={cpLocked("file_refinery")} lockStyle={cpLockStyle} onLockedClick={() => cpOpenGate("file_refinery")}>
            <LLMGuardWrap surface="refinery" siloId={siloId}>
              <button onClick={run} disabled={running || populatedCount === 0 || !!activeRefineryJobId} data-testid="file-refinery-run"
                className="px-5 py-2 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] text-sm disabled:opacity-60">
                {activeRefineryJobId ? "Refinery running…" : "Start refinery"}
              </button>
            </LLMGuardWrap>
          </LockGate>
        </div>
        {/* May 25, 2026 — Boost for Refinery jobs. Backend picks up the
            active boost and uses a much bigger chunk size when the
            picked model has the context for it (800k chars on Gemini Pro,
            200k on Sonnet/GPT). */}
        <BoostButton
          surface="refinery"
          gateSku="refinery_boost"
          inputChars={[slots[1], slots[2], slots[3]].filter(Boolean).reduce((acc, s) => acc + s.length, 0)}
          testidPrefix="refinery-boost"
          siloId={siloId}
          onBoostChange={({ boost }) => setRefineryBoost(boost || null)}
          onRequestTopup={(sid) => setTopupOpenSiloId(sid)}
        />
      </div>

      {/* May 25, 2026 — Voucher top-up modal triggered from boost gate. */}
      <VoucherTopupModal
        open={!!topupOpenSiloId}
        prefocusedSiloId={topupOpenSiloId}
        onClose={() => setTopupOpenSiloId(null)}
        onSuccess={() => setTopupOpenSiloId(null)}
      />

      {/* Active refinery job — replaces the chunk-progress block when running */}
      {activeRefineryJobId && (
        <div className="mt-4">
          <RefineryProgressCard
            jobId={activeRefineryJobId}
            onClose={() => setActiveRefineryJobId(null)}
            onComplete={(job) => {
              // "Drop where chosen": if the run auto-committed to a tier, the
              // result already landed there — don't also dump it into staging.
              // Otherwise (Stage only) append the streamed buffer into staging.
              if (job?.auto_commit_to_tier) {
                toast.success(`Refinery results committed to Tier ${job.auto_commit_to_tier}.`);
              } else if (job?.staging_buffer) {
                setStaging((s) => s ? `${s}\n\n${job.staging_buffer}` : job.staging_buffer);
                toast.success("Refinery results appended to staging.");
              }
            }}
          />
        </div>
      )}

      {/* iter-35c: chunk-progress block from Workbench is intentionally
          omitted — File Refinery's progress is rendered by
          RefineryProgressCard above (one progress UI, not two). */}

      {/* Staging area — Iter160m (Randy pt-siloart-loosetext-sweep) —
          `<div>` staging labels are LOOSE TEXT on the canvas. Bare divs
          are excluded from the auto-loose rule (too many false positives
          via wrappers), so we mark these explicitly with `.silo-loose`. */}
      <div className="mt-8" data-testid="staging-area">
        <div className="flex items-center justify-between mb-2 gap-3">
          <div className="text-xs uppercase tracking-[0.2em] text-[#6B6A68] silo-loose">Staging area</div>
          <div className="flex items-center gap-2 flex-1 max-w-xs">
            {staging && <TextSearch value={staging} textareaRef={stagingRef} testid="staging-search" />}
          </div>
          <div className="text-xs text-[#6B6A68] silo-loose">{staging.length.toLocaleString()} chars</div>
        </div>
        <textarea ref={stagingRef} value={staging} onChange={(e) => { recordStep("pause_for_editing", {}); setStaging(e.target.value); }}
          rows={Math.min(14, Math.max(6, Math.ceil((staging.length || 0) / 200)))}
          data-testid="staging-textarea"
          className="w-full bg-white border hairline rounded-lg px-4 py-3 font-mono text-xs leading-relaxed focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]"
          placeholder="Stage promising results here. Append from the Result area, edit freely, then send to a slot or commit." />
        {staging && (
          <ActionRow
            text={staging}
            testidPrefix="stg"
            defaultTitle="Staged content"
            isStaging={true}
            sendTo={sendStagingToSlot}
            extra={
              <button onClick={() => askConfirm({ title: "Clear staging area?", message: "Clears all content from staging.", danger: true, confirmLabel: "Clear", onConfirm: () => setStaging("") })}
                data-testid="stg-clear"
                className="text-sm px-4 py-2 rounded-md border hairline bg-white hover:border-[#C16E5A] hover:text-[#C16E5A] flex items-center gap-1.5">
                <Trash size={14} /> Clear
              </button>
            }
          />
        )}
      </div>

      {/* Result — only visible when synchronous output exists. iter-35c:
          File Refinery is refinery-only, so `output` is never populated and
          this section stays hidden. Kept as a no-op so the rest of the file
          (ActionRow imports, output state) remains structurally identical to
          Workbench until we do a future cleanup pass. */}
      {output && (
      <div className="mt-8">
        <div className="flex items-center justify-between mb-2 gap-3">
          <div className="text-xs uppercase tracking-[0.2em] text-[#6B6A68] silo-loose">Result</div>
          <div className="flex items-center gap-3">
            {lastChunkRanges && Object.keys(lastChunkRanges).length > 0 && (
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#4A5D4E]" data-testid="result-chunk-meta">
                ↳ from{" "}
                {Object.entries(lastChunkRanges).map(([n, r], i, arr) => (
                  <span key={n}>Slot {n} {r.start.toLocaleString()}–{r.end.toLocaleString()}{i < arr.length - 1 ? " · " : ""}</span>
                ))}
              </div>
            )}
            <button onClick={appendToStaging} data-testid="wb-append-staging-top"
              className="text-xs px-3 py-1.5 rounded-md border hairline bg-white hover:border-[#4A5D4E] hover:text-[#4A5D4E] flex items-center gap-1.5">
              <ArrowUp size={12} weight="bold" /> Append to staging
            </button>
          </div>
        </div>
        <div
          className="bg-white border hairline rounded-lg p-5 min-h-[200px] max-h-[1200px] resize-y overflow-auto whitespace-pre-wrap text-sm text-[#1C1B1A] font-mono leading-relaxed relative"
          data-testid="file-refinery-output"
          style={{ resize: "vertical" }}
        >
          {output}
          <ResizeHint title="Drag bottom-right to resize the result pane" />
        </div>
        <ActionRow
          text={output}
          testidPrefix="wb"
          defaultTitle="Workbench output"
          sendTo={sendOutputToSlot}
          extra={
            <>
              <button onClick={appendToStaging} data-testid="wb-append-staging"
                className="text-sm px-4 py-2 rounded-md border hairline bg-white hover:border-[#4A5D4E] hover:text-[#4A5D4E] flex items-center gap-1.5">
                <ArrowUp size={14} weight="bold" /> Append to staging
              </button>
              <button onClick={() => askConfirm({ title: "Clear results?", message: "Clears the Result area. Staging area is untouched.", danger: true, confirmLabel: "Clear", onConfirm: () => setOutput("") })}
                data-testid="wb-output-clear"
                className="text-sm px-4 py-2 rounded-md border hairline bg-white text-[#6B6A68] hover:text-[#C16E5A] hover:border-[#C16E5A] flex items-center gap-1.5">
                Clear
              </button>
            </>
          }
        />
      </div>
      )}

      {/* AI Assistant — the SAME upgraded assistant as the Workbench (Full-Silo
          knowledge included), gated by the same Workbench-Suite unlock. */}
      <div className="mt-8" data-testid="ai-buddy-mount-file-refinery">
        <AiBuddy
          surface="file-refinery"
          siloId={siloId}
          siloSlug={siloSlug}
          showSiloControls
          title="AI Assistant"
          subtitle="Brainstorm · Full-Silo aware"
          emptyHint="Ask Assistant anything. Brainstorm, expand thoughts, create outlines — or flip on Full Silo to have it read your silo's library. Append its reply to your staging area above."
          inputPlaceholder="Ask anything. Turn on Full Silo to ground it in your silo's knowledge."
          onCommitToSilo={(text) => setCommitState({ open: true, text, defaultTitle: "" })}
          onAppendToStaging={(text) => {
            setStaging((s) => (s ? s + "\n\n" + text : text));
            toast.success("Appended Assistant reply to staging.");
            setTimeout(() => stagingRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 80);
          }}
        />
      </div>

      {/* YouTube Tool — pull metadata, transcripts, comments. Per-creator BYOK key. */}
      <div className="mt-6" data-testid="youtube-tool-mount">
        <YouTubeTool
          siloId={siloId}
          slotCount={1}
          surface="file_refinery"
          clearRequest={childClearNonce}
          user={ytUser}
          macroApi={macroApi}
          workbenchMacroApiRef={refineryMacroApiRef}
          onUserUpdated={(u) => { setYtUser(u); try { setUser(u); } catch { /* ignore */ } }}
          onAppendToStaging={(text) => {
            setStaging((s) => (s ? s + "\n\n" + text : text));
            toast.success(`Appended to staging · ${text.length.toLocaleString()} chars`);
            setTimeout(() => stagingRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 80);
          }}
          onSendToSlot={(n, text) => {
            if (!slotActive[n]) {
              toast.error(`Slot ${n} is deactivated — reactivate it first.`);
              return;
            }
            setSlots((s) => ({ ...s, [n]: text }));
            toast.success(`Sent to Slot ${n} · ${text.length.toLocaleString()} chars`);
          }}
          onSendToAssistant={(text) => {
            import("@/components/AiBuddy").then(({ seedAssistant }) => {
              seedAssistant("file-refinery", text);
              toast.success("Seeded into AI Assistant — open the panel to ask your question.");
            });
          }}
        />
      </div>

      {/* Macros — record a big-file workflow once, replay it on the next file.
          Tailored to the Refinery's async background-job flow. */}
      <div id="refinery-macros-anchor">
      <RefineryMacros
        busRef={macroBusRef}
        ytApiRef={ytApiRef}
        macroApiRef={refineryMacroApiRef}
        mode={macroMode}
        setMode={setMacroMode}
        siloId={siloId}
        recordingRef={recordingRef}
        recordBufRef={recordBufRef}
        lastStepAtRef={lastStepAtRef}
        recordCount={macroRecordCount}
        setRecordCount={setMacroRecordCount}
      />
      </div>

      {/* Customize */}
      {showCustomize && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="customize-dialog">
          <div className="absolute inset-0 bg-[#1C1B1A]/40 backdrop-blur-sm" onClick={() => setShowCustomize(false)} />
          <div className="modal-safe relative z-10 w-full max-w-xl bg-white border hairline rounded-xl shadow-2xl overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b hairline">
              <div className="font-serif-display text-lg">Custom commands</div>
              <button onClick={() => setShowCustomize(false)} className="p-2 rounded-md text-[#6B6A68] hover:bg-[#F0F1EE]"><X size={18} /></button>
            </div>
            <div className="p-6 max-h-[70vh] overflow-y-auto space-y-5">
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-[#6B6A68] mb-2">Your custom commands</div>
                {custom.length === 0 ? (
                  <div className="text-sm text-[#6B6A68]">No custom commands yet. Create one below.</div>
                ) : (
                  <ul className="divide-y hairline border hairline rounded-md">
                    {custom.map((c) => (
                      <li key={c.id} className={`p-3 flex items-start gap-3 ${editingId === c.id ? "bg-[#F0F1EE]" : ""}`} data-testid={`cmd-row-${c.id}`}>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-[#1C1B1A]">{c.name}</div>
                          <div className="text-xs text-[#6B6A68] mt-1 line-clamp-2">{c.instructions}</div>
                        </div>
                        <button onClick={() => openEdit(c)} data-testid={`cmd-edit-${c.id}`}
                          className="p-1.5 rounded-md text-[#6B6A68] hover:text-[#4A5D4E] hover:bg-[#FDFCF8]"><PencilSimple size={14} weight="duotone" /></button>
                        <button onClick={() => deleteCustom(c)} data-testid={`cmd-delete-${c.id}`}
                          className="p-1.5 rounded-md text-[#6B6A68] hover:text-[#C16E5A] hover:bg-[#FDFCF8]"><Trash size={14} weight="duotone" /></button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="border-t hairline pt-5">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs uppercase tracking-[0.2em] text-[#6B6A68]">{editingId ? "Edit command" : "Create new"}</div>
                  {editingId && <button onClick={cancelEdit} className="text-xs text-[#6B6A68] hover:text-[#1C1B1A]">Cancel edit</button>}
                </div>
                <label className="block text-sm text-[#6B6A68] mt-1 mb-1.5">Name</label>
                <input data-testid="cmd-draft-name" value={draftCmd.name} onChange={(e) => setDraftCmd({ ...draftCmd, name: e.target.value })}
                  className="w-full bg-white border hairline rounded-md px-4 py-2.5 focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]" />
                <label className="block text-sm text-[#6B6A68] mt-3 mb-1.5">Instructions</label>
                <textarea data-testid="cmd-draft-instructions" rows={5} value={draftCmd.instructions}
                  onChange={(e) => setDraftCmd({ ...draftCmd, instructions: e.target.value })}
                  className="w-full bg-white border hairline rounded-md px-4 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]" />
                <div className="mt-3 flex justify-end">
                  <button onClick={saveDraft} disabled={savingCmd} data-testid="cmd-draft-save"
                    className="px-5 py-2 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] text-sm disabled:opacity-60">
                    {savingCmd ? "Saving…" : (editingId ? "Save changes" : "Save command")}
                  </button>
                </div>
              </div>

              {/* AI Command Assist — pastes drafts straight into Instructions */}
              <div className="border-t hairline pt-5" data-testid="ai-buddy-mount-commands">
                <AiBuddy
                  surface="commands-editor"
                  siloId={siloId}
                  compact
                  title="AI Command Assist"
                  subtitle="Command-drafting helper"
                  emptyHint="Tell Command Assist your intention — have it make a short, accurate command to achieve it!"
                  onPasteToInstructions={(text) => {
                    setDraftCmd((d) => ({ ...d, instructions: text }));
                    toast.success("Pasted into Instructions field.");
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modals replacing window.confirm/prompt */}
      <ConfirmModal {...confirmState} onClose={() => setConfirmState({ open: false })} />
      <PromptModal {...promptState} onClose={() => setPromptState({ open: false })} />
      <DocSaveDialog
        open={commitState.open}
        onClose={() => setCommitState({ open: false, text: "", defaultTitle: "" })}
        content={commitState.text}
        sourceSiloId={siloId}
        sourceDoc={null}
        silos={[{ id: siloId, name: siloName || "this silo" }]}
        lockSilo
        defaultTitle={commitState.defaultTitle}
        initialTier={2}
        sourceSurface="impromptu_staging"
        dialogTitle={siloName ? `Commit to "${siloName}"` : "Commit to silo"}
        checkOversize={(content, tier) => needsSplit(content, tier)}
        onOversize={({ title, tier }) => {
          setSplitState({ open: true, content: commitState.text, siloId, tier, title: title || "", op: (commands.find((c) => c.id === selectedCmdId)?.name) || null });
          setCommitState({ open: false, text: "", defaultTitle: "" });
        }}
        onSaved={() => {}}
      />
      <SplitAndPushModal
        open={splitState.open}
        content={splitState.content}
        defaultTitle={splitState.title}
        siloId={splitState.siloId}
        siloName={siloName}
        initialTier={splitState.tier}
        sourceSurface="impromptu_staging"
        sourceOperation={null}
        onClose={() => setSplitState((s) => ({ ...s, open: false }))}
        onComplete={() => setSplitState((s) => ({ ...s, open: false }))}
      />

      {/* Quick-nav (Jul 2026, Randy): transparent floating carets — TOP jumps to
          the bottom, BOTTOM jumps to the top. Same logic as the Workbench. */}
      {/* AI YouTube Comment Replies — Channel 2 (duplicate of the Workbench
          Channel 1 card). Static visual for now — OAuth + Stripe wiring lands
          later. (Randy, Jul 2026 — FEATURE_YT_AI_REPLIES_SPEC.md) */}
      <YTRepliesCard channelSlot="channel2" siloId={siloId} />
      <YTRepliesConsoleCard channelSlot="channel2" siloId={siloId} siloSlug={siloSlug} />
      <YTRepliesRateLimitsCard channelSlot="channel2" siloId={siloId} />

      <div data-testid="fr-bottom-anchor" className="h-0" />
      <button
        type="button"
        onClick={scrollRefineryToBottom}
        data-testid="refinery-jump-bottom"
        title="Jump to the bottom of the Refinery"
        style={{ top: "calc(7.5rem + env(safe-area-inset-top))" }}
        className="fixed left-1/2 -translate-x-1/2 z-[100] w-[26px] h-[26px] rounded-full bg-transparent border hairline backdrop-blur-sm text-[#4A5D4E] hover:border-[#4A5D4E] active:scale-95 transition-transform flex items-center justify-center"
      >
        <CaretDown size={11} weight="bold" />
      </button>
      <button
        type="button"
        onClick={scrollRefineryToTop}
        data-testid="refinery-jump-top"
        title="Jump to the top of the Refinery"
        style={{ bottom: "calc(0.9rem + env(safe-area-inset-bottom))" }}
        className="fixed left-1/2 -translate-x-1/2 z-[100] w-[26px] h-[26px] rounded-full bg-transparent border hairline backdrop-blur-sm text-[#4A5D4E] hover:border-[#4A5D4E] active:scale-95 transition-transform flex items-center justify-center"
      >
        <CaretUp size={11} weight="bold" />
      </button>
    </div>
  );
}
